import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Globe, Menu, X } from "lucide-react";
import { config } from "../utils/config.ts";
import { LANGUAGES, currentLang, setLanguage } from "../utils/translate";

/* The marketing site's announcement bar, header and footer, ported so the dashboard sign-in page looks exactly
   like the website (same links, sizes, hover pill and language menu). Links point at the marketing site. */
const site = config.backendUrl;
const PLAY_STORE = "https://play.google.com/store/apps/details?id=co.ke.twachanga";
const brand = { fontFamily: "'Google Sans Flex', 'Google Sans', Roboto, system-ui, sans-serif" };
const LINKS = [["Home", "/"], ["Features", "/features"], ["Pricing", "/pricing"], ["FAQs", "/faq"], ["Blog", "/blog"], ["Bulk SMS", "/sms"], ["About", "/about"]];

/* The dashboard theme forces border colours with !important, so set ours inline with the same flag. */
const bc = (color) => (el) => { if (el) el.style.setProperty("border-color", color, "important"); };
const hoverBc = (on, off) => ({ onMouseEnter: (e) => e.currentTarget.style.setProperty("border-color", on, "important"), onMouseLeave: (e) => e.currentTarget.style.setProperty("border-color", off, "important") });

const ChromeCss = () => (
  <style>{`html .sc-ink{border-color:#1D192B!important}html .sc-outline{border-color:#C4C7C5!important}html .sc-grey{border-color:#E0E0E0!important}html .sc-card{border-color:#D5E0F5!important}html .sc-card:hover{border-color:#1A73E8!important}`}</style>
);

export function Banner() {
  const [open, setOpen] = useState(true);
  if (!open) return null;
  return (
    <div className="relative bg-[#E8DEF8] text-[#1D192B]">
      <ChromeCss />
      <div className="mx-auto flex w-full max-w-[1280px] items-center justify-between gap-4 px-4 py-2.5 pr-12 text-[15px] lg:px-6">
        <span className="font-medium">The updated Twachanga Android app is now on Google Play.</span>
        <a ref={bc("#1D192B")} href={PLAY_STORE} target="_blank" rel="noopener noreferrer" className="hidden shrink-0 rounded-full border sc-ink border-[#1D192B] px-4 py-1 text-sm font-medium hover:bg-[#ffffff] sm:inline-block">Get the app →</a>
      </div>
      <button type="button" aria-label="Dismiss" onClick={() => setOpen(false)} className="absolute right-3 top-1/2 grid size-8 -translate-y-1/2 place-items-center rounded-full hover:bg-[#D0BCFF]"><X size={16} /></button>
    </div>
  );
}

function LanguageMenu() {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("en");
  const ref = useRef(null);
  useEffect(() => setCode(currentLang()), []);
  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  const active = LANGUAGES.find((l) => l.code === code) || LANGUAGES[0];
  return (
    <div className="notranslate relative" translate="no" ref={ref}>
      <button type="button" aria-label="Choose language" aria-expanded={open} onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 rounded-full px-3 py-2 text-[15px] font-bold text-[#202124] transition-colors hover:bg-[#E8F0FE] hover:text-[#0B57D0]">
        <Globe size={18} /><span className="hidden sm:inline">{active.native}</span>
      </button>
      {open && (
        <ul className="absolute right-0 top-full z-50 mt-1 min-w-[200px] rounded-2xl bg-[#ffffff] py-2 shadow-[0_4px_20px_rgba(60,64,67,.25)]">
          {LANGUAGES.map((l) => (
            <li key={l.code}>
              <button type="button" onClick={() => { setOpen(false); setCode(l.code); setLanguage(l.code); }}
                className={"flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-[#F1F3F4] " + (l.code === code ? "bg-[#E8F0FE]" : "")}>
                <span className="font-semibold text-[#202124]">{l.native}</span><span className="text-xs text-[#5F6368]">{l.label}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(null);
  const [pill, setPill] = useState(null);
  const navRef = useRef(null);
  const items = useRef({});
  // The hover pill glides between links (same effect as the website, done with a CSS transition).
  useLayoutEffect(() => {
    const el = hover && items.current[hover];
    if (el) setPill({ left: el.offsetLeft, width: el.offsetWidth });
    else setPill((p) => (p ? { ...p, hidden: true } : p));
  }, [hover]);
  return (
    <header className="sticky top-0 z-50 bg-[#ffffff] shadow-[0_2px_8px_rgba(60,64,67,0.16)]" style={brand}>
      <ChromeCss />
      <div className="mx-auto flex h-16 w-full max-w-[1280px] items-center gap-3 px-4 lg:px-6 xl:gap-6">
        <a href={site + "/"} aria-label="Twachanga home" className="flex shrink-0 items-center"><img src={process.env.PUBLIC_URL + "/assets/auth/logo.png"} alt="Twachanga" className="h-11 w-auto" /></a>
        <nav ref={navRef} onMouseLeave={() => setHover(null)}
          className="relative ml-1 hidden h-16 min-w-0 flex-1 items-center overflow-hidden whitespace-nowrap lg:flex xl:ml-4 [mask-image:linear-gradient(to_right,#000_calc(100%-48px),transparent)]">
          {pill && <span aria-hidden className="pointer-events-none absolute top-1/2 h-10 -translate-y-1/2 rounded-full bg-[#E8F0FE] transition-all duration-300 ease-out" style={{ left: pill.left, width: pill.width, opacity: pill.hidden ? 0 : 1 }} />}
          {LINKS.map(([label, path]) => (
            <a key={path} ref={(el) => (items.current[path] = el)} href={site + path} onMouseEnter={() => setHover(path)} onFocus={() => setHover(path)} onBlur={() => setHover(null)}
              className={"relative px-2.5 py-2.5 text-[14px] font-bold tracking-[-0.01em] transition-colors duration-200 xl:px-4 xl:text-[15px] " + (hover === path ? "text-[#0B57D0]" : "text-[#202124]")}>{label}</a>
          ))}
        </nav>
        <div className="ml-auto flex shrink-0 items-center gap-1 whitespace-nowrap sm:gap-2">
          <LanguageMenu />
          <a href="/login" className="hidden px-2 text-[15px] font-bold text-[#0B57D0] hover:underline xl:inline xl:px-3">Log in</a>
          <a ref={bc("#C4C7C5")} href={PLAY_STORE} target="_blank" rel="noopener noreferrer" className="hidden h-[38px] items-center rounded-full border sc-outline border-[#C4C7C5] bg-[#ffffff] px-5 text-sm font-semibold text-[#1A73E8] hover:bg-[#F1F6FE] xl:inline-flex">Get the app</a>
          <a href={site + "/register"} className="inline-flex h-[38px] items-center rounded-full bg-[#1A73E8] px-5 text-sm font-semibold text-[#ffffff] hover:bg-[#1765CC]">Sign up</a>
          <button type="button" aria-label="Menu" onClick={() => setOpen(!open)} className="grid size-10 place-items-center rounded-full text-[#5F6368] hover:bg-[#F1F3F4] lg:hidden">{open ? <X size={20} /> : <Menu size={20} />}</button>
        </div>
      </div>
      {open && (
        <nav ref={bc("#E0E0E0")} className="sc-grey border-t bg-[#ffffff] px-4 pb-4 lg:hidden">
          {LINKS.map(([label, path]) => <a key={path} ref={bc("#E0E0E0")} href={site + path} className="sc-grey block border-b py-3 text-base font-bold text-[#202124]">{label}</a>)}
          <a href="/login" className="block py-3 text-base font-bold text-[#0B57D0]">Log in</a>
        </nav>
      )}
    </header>
  );
}

export function SiteFooter() {
  const asset = (f) => site + "/assets/" + f;
  const platforms = [
    { href: PLAY_STORE, title: "Mobile App", sub: "Download from Play Store", icon: <img src={asset("download%20(2).png")} alt="Get it on Google Play" className="size-9 object-contain" /> },
    { href: "https://wa.me/254781856551", title: "WhatsApp", sub: "Use WhatsApp service", icon: <i className="fab fa-whatsapp text-[28px] text-[#25D366]" /> },
    { href: site + "/register", title: "Web App", sub: "Access from any browser", icon: <i className="fas fa-globe text-[26px] text-[#1A73E8]" /> },
  ];
  const product = [["Features", "/features"], ["Pricing", "/pricing"], ["Testimonials", "/"], ["Bulk SMS", "/sms"]];
  const resources = [["About Us", "/about"], ["FAQs", "/faq"], ["Blog", "/blog"], ["Contact", "/contact"], ["Privacy Policy", "/privacy-policy"]];
  const h = { ...brand, fontWeight: 700 };
  return (
    <footer ref={bc("#E0E0E0")} className="sc-grey border-t border-[#E0E0E0] bg-[#F4F8FF] text-[#202124]" style={brand}>
      <ChromeCss />
      <div className="mx-auto w-full max-w-[1280px] px-4 py-16 lg:px-6">
        <div className="grid gap-12 lg:grid-cols-[1.1fr_1fr_1.1fr]">
          <div>
            <img src={process.env.PUBLIC_URL + "/assets/auth/logo.png"} alt="Twachanga" className="h-11 w-auto" />
            <p className="mt-5 max-w-sm text-[15px] leading-6 text-[#3C4043]">Innovative group management solutions for modern communities and chamas in Kenya and beyond.</p>
            <ul className="mt-6 space-y-3 text-[15px] text-[#3C4043]">
              <li className="flex items-center gap-3"><i className="fas fa-envelope w-4 text-[#1A73E8]" /> help@twachanga.co.ke</li>
              <li className="flex items-center gap-3"><i className="fas fa-phone w-4 text-[#1A73E8]" /> +254739264525</li>
            </ul>
          </div>
          <div>
            <h3 className="text-lg" style={h}>Get Twachanga</h3>
            <p className="mt-1 text-sm text-[#5F6368]">Available on all platforms</p>
            <div className="mt-5 grid gap-3">
              {platforms.map((p) => (
                <a key={p.title} ref={bc("#D5E0F5")} {...hoverBc("#1A73E8", "#D5E0F5")} href={p.href} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 sc-card rounded-2xl border border-[#D5E0F5] bg-[#ffffff] p-4 transition-colors hover:border-[#1A73E8]">
                  <span className="grid size-12 shrink-0 place-items-center rounded-xl bg-[#EAF1FF]">{p.icon}</span>
                  <span><span className="block font-semibold text-[#202124]">{p.title}</span><span className="block text-sm text-[#5F6368]">{p.sub}</span></span>
                </a>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-8">
            <div>
              <h4 className="text-base" style={h}>Product</h4>
              <ul className="mt-4 space-y-3 text-[15px]">{product.map(([l, p]) => <li key={l}><a href={site + p} className="text-[#3C4043] hover:text-[#0B57D0]">{l}</a></li>)}</ul>
            </div>
            <div>
              <h4 className="text-base" style={h}>Resources</h4>
              <ul className="mt-4 space-y-3 text-[15px]">{resources.map(([l, p]) => <li key={l}><a href={site + p} className="text-[#3C4043] hover:text-[#0B57D0]">{l}</a></li>)}</ul>
            </div>
            <div className="col-span-2 text-center">
              <h4 className="text-base" style={h}>Twachanga Bulk SMS</h4>
              <a href="https://sms.twachanga.co.ke/auth/login" target="_blank" rel="noopener noreferrer" aria-label="Twachanga SMS Portal" className="mt-3 inline-block"><img src={asset("sms_logo.png")} alt="Twachanga SMS" className="mx-auto w-40 max-w-[60%]" /></a>
            </div>
          </div>
        </div>
        <div ref={bc("#D5E0F5")} className="mt-14 flex flex-col items-center justify-between sc-card gap-5 border-t border-[#D5E0F5] pt-8 md:flex-row">
          <div className="flex items-center gap-5 text-xl">
            <a href="https://www.youtube.com/@twachanga" aria-label="YouTube" className="text-[#202124] hover:text-[#FF0000]"><i className="fab fa-youtube" /></a>
            <a href="https://wa.me/254739264525" aria-label="WhatsApp" className="text-[#202124] hover:text-[#25D366]"><i className="fab fa-whatsapp" /></a>
            <a href="https://www.facebook.com/twachanga" aria-label="Facebook" className="text-[#202124] hover:text-[#1A73E8]"><i className="fab fa-facebook-f" /></a>
          </div>
          <div className="text-center text-sm md:text-right">
            <p className="text-[#5F6368]">Powered by AI for smarter, simpler group management.</p>
            <p className="mt-1 text-[#5F6368]">&copy; {new Date().getFullYear()} Twachanga. All rights reserved.</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
